import type { ReactNode } from 'react';
import { toast } from 'react-toastify';

// Thin adapter over the single, app-global react-toastify instance (mounted once in
// App.tsx). Previously this rendered a modal-scoped stack INSIDE the Account Settings
// modal, which made notifications feel attached to the modal. Now every success /
// error / warning / info routes through the one global toaster, so billing feedback
// appears in the same place as the rest of the app. The Provider/Viewport are kept as
// no-op passthroughs for backwards compatibility with any remaining call sites.

export type NotifyType = 'success' | 'error' | 'warning' | 'info';

interface NotifyApi {
  notify: (type: NotifyType, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const api: NotifyApi = {
  notify: (type, message) => toast[type](message),
  success: (message) => toast.success(message),
  error: (message) => toast.error(message),
  warning: (message) => toast.warning(message),
  info: (message) => toast.info(message),
};

/** Access the app-global notifier. Renders through the single react-toastify container. */
export function useNotify(): NotifyApi {
  return api;
}

/** @deprecated No longer needed — notifications are global. Kept as a passthrough. */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/** @deprecated No longer needed — the global toaster (App.tsx) renders notifications. */
export function NotificationsViewport() {
  return null;
}
